import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programs, sites, WITHHELD_LABEL } from '@cwos/api-client';
import type { programs as ProgramsNS } from '@cwos/api-client';
import {
  FilteredEmpty,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { populationNote, type Population } from '../figureState.js';
import { statedPopulation } from '../readScope.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';
import {
  EMPTY_PROGRAM_DRAFT,
  emptyReason,
  hasNextPage,
  hasPreviousPage,
  programCreatePayload,
  programSitesLabel,
  rangeLabel,
  statusBadge,
  validateProgramDraft,
  type ProgramDraft,
} from './organizationLogic.js';
import { LoadFailure } from '../LoadFailure.js';

const PAGE_SIZE = 25;

/**
 * Programs — the list, the search, and the create flow.
 *
 * `program:read` has had no endpoint behind it until this track; this screen is
 * the read surface. Everything is a real read over `/programs`: server-side
 * search, status filtering, sorting and pagination, because an organization with
 * 300 programs must not have all of them shipped to the browser to be filtered
 * there.
 *
 * Creating is deliberately small — a name is the only requirement. Sites,
 * people, a lead and dates are all things an administrator adds afterwards from
 * the program's own screen, where there is room to do it properly.
 */
export function ProgramsSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('program:manage');

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'default' | 'active' | 'inactive' | 'retired' | 'all'>(
    'default',
  );
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ProgramDraft>(EMPTY_PROGRAM_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);

  const listQuery = {
    q: query.trim() || undefined,
    status: status === 'default' ? undefined : status,
    limit: PAGE_SIZE,
    offset,
  };

  const list = useQuery({
    queryKey: ['programs', 'list', listQuery],
    queryFn: () => programs.list(listQuery),
  });

  /**
   * WHOSE PROGRAMS THIS IS, AS THE SERVER SAID IT.
   *
   * `GET /programs` is `guard(..., 'program:read', { allowScoped: true })` and
   * passes `accessibleProgramIds(principal.authz)` into `listPrograms`, which
   * puts those ids into the WHERE clause and hands back
   * `scope: 'organization' | 'programs'` saying which it did
   * (programs/service.ts:214-273, routes.ts:440). The field has been on the
   * payload and on `ProgramListResult` in `@cwos/api-client` the whole time, and
   * this screen read none of it — every sentence on it was written as though the
   * answer were always `'organization'`.
   *
   * The server's own comment on the field is the case for reading it: "`total`
   * is honest either way — it is now counted over the same `where` the page is
   * drawn from — but '4 programs' under a heading that says Programs is a
   * statement about the ORGANIZATION, and for a scoped reader it is a false one
   * while every row on the list is true."
   *
   * Four sentences on this screen were that statement: the card heading ("All
   * programs"), the table caption ("Programs in this organization"), the pager's
   * count, and — worst — the empty state, "No programs yet", which is the
   * withheld-as-absent inversion the withholding contract ranks first. A
   * program-scoped reader whose one program is retired saw an organization
   * declared programless and an invitation to create its first.
   *
   * `undefined` is neither frame. In flight, failed, or refused, the honest
   * answer is `unknown` — `figureState` has the word, and rendering the guess as
   * `organization` is what put the false sentence on screen in the first place.
   * `statedPopulation` is where that rule is written down, and passing
   * `list.data?.scope` rather than a fallback is what hands it the case.
   *
   * THIS CONVERSION USED TO BE WRITTEN OUT HERE. `StatedScope` was
   * `'organization' | 'caseload'` and this domain's narrowed arm is spelled
   * `'programs'`, so the screen compared the string itself. Both spellings mean
   * "a subset, not the organization" and the divergence was in the noun only —
   * but a second `=== 'organization'` living in a screen file is a second place
   * for the rule to be stated, and the two only have to disagree once. The union
   * carries `'programs'` now and the screen asks the shared helper.
   */
  const population: Population = statedPopulation(
    list.data?.scope,
    'the programs you were granted',
  );
  const wholeOrg = population.kind === 'organization';
  const scopeNote = populationNote(population);

  // Only needed while the create panel is open — a site picker with nothing to
  // pick is not worth a request on every visit to the list.
  const siteOptions = useQuery({
    queryKey: ['sites', 'options'],
    queryFn: () => sites.list({ limit: 200, sort: 'name' }),
    enabled: creating && canManage,
  });

  const create = useMutation({
    mutationFn: () => programs.create(programCreatePayload(draft)),
    onSuccess: (program) => {
      setCreating(false);
      setDraft(EMPTY_PROGRAM_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['programs'] });
      navigate(`/settings/programs/${program.id}`);
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : 'We couldn’t create that program.');
    },
  });

  const errors = validateProgramDraft(draft);
  const canSubmit = Object.keys(errors).length === 0 && draft.name.trim().length > 0;

  const columns: Column<ProgramsNS.Program>[] = [
    {
      key: 'name',
      header: 'Program',
      render: (p) => (
        <span>
          <span style={{ fontWeight: 600 }}>{p.name}</span>
          {p.code ? <span className="rn-muted"> · {p.code}</span> : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => {
        const badge = statusBadge(p.status);
        return (
          <Pill variant={badge.variant} dot>
            {badge.label}
          </Pill>
        );
      },
    },
    // `?? '—'` reported a withheld lead as no lead at all. An em dash in a
    // "Program lead" column is an affirmative statement that the program is
    // unowned, and `programLeadNameWithheld` is the only thing that separates
    // that from a name being kept back.
    {
      key: 'lead',
      header: 'Program lead',
      render: (p) =>
        p.programLeadNameWithheld ? (
          <span className="rn-muted">{WITHHELD_LABEL}</span>
        ) : (
          (p.programLeadName ?? '—')
        ),
    },
    { key: 'sites', header: 'Sites', render: (p) => programSitesLabel(p) },
    {
      key: 'people',
      header: 'People',
      numeric: true,
      render: (p) => String(p.activeWorkerCount),
    },
  ];

  const reason = emptyReason(query, status);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Programs"
        blurb="The operational units your work belongs to. Programs organise people, schedules and reporting — you define your own."
      />

      <SettingsCard>
        <div className="rn-card-head">
          {/* "All programs" is a claim about the organization. It is the
              server's to make, and it makes it in `scope`. */}
          <h2 className="rn-card-title">{wholeOrg ? 'All programs' : 'Programs'}</h2>
          {canManage ? (
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
              aria-expanded={creating}
            >
              {creating ? 'Cancel' : 'Create program'}
            </button>
          ) : null}
        </div>

        {creating && canManage ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <div className="org-create-grid">
              <SettingsField label="Name" error={errors['name']}>
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.name}
                  autoFocus
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label="Short code (optional)"
                hint="A quick reference like PRS. It doesn’t have to be unique to be useful, but two programs can’t share one."
                error={errors['code']}
              >
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              </SettingsField>
            </div>
            <SettingsField label="Description (optional)" error={errors['description']}>
              <textarea
                style={{ marginBottom: 0 }}
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </SettingsField>

            <fieldset className="org-fieldset">
              <legend className="rn-muted">Sites (optional)</legend>
              {siteOptions.isLoading ? (
                <SkeletonSet surface="table" rows={2} />
              ) : siteOptions.isError && siteOptions.data === undefined ? (
                <p className="rn-muted" style={{ fontSize: 13 }} role="status">
                  We couldn’t load your sites, so none are offered here. You can attach them later.
                </p>
              ) : (siteOptions.data?.sites.length ?? 0) === 0 ? (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  No sites yet. You can add them later, or run this program with no fixed site.
                </p>
              ) : (
                <div className="org-checklist">
                  {(siteOptions.data?.sites ?? []).map((site) => {
                    const checked = draft.siteIds.includes(site.id);
                    return (
                      <label key={site.id} className="org-check">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const siteIds = e.target.checked
                              ? [...draft.siteIds, site.id]
                              : draft.siteIds.filter((id) => id !== site.id);
                            setDraft({
                              ...draft,
                              siteIds,
                              primarySiteId: siteIds.includes(draft.primarySiteId)
                                ? draft.primarySiteId
                                : (siteIds[0] ?? ''),
                            });
                          }}
                        />
                        <span>{site.name}</span>
                        {site.city ? <span className="rn-muted"> · {site.city}</span> : null}
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>

            {createError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {createError}
              </p>
            ) : null}

            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSubmit || create.isPending}
              >
                {create.isPending ? 'Creating…' : 'Create program'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setCreating(false);
                  setDraft(EMPTY_PROGRAM_DRAFT);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        <div className="org-filters" role="search">
          <label className="org-filter">
            <span className="rn-muted">Search</span>
            <input
              style={{ marginBottom: 0 }}
              type="search"
              placeholder="Name or code"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOffset(0);
              }}
            />
          </label>
          <label className="org-filter">
            <span className="rn-muted">Status</span>
            <select
              style={{ marginBottom: 0 }}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setOffset(0);
              }}
            >
              <option value="default">Active and inactive</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
              <option value="retired">Retired only</option>
              <option value="all">Everything</option>
            </select>
          </label>
        </div>

        {list.isLoading ? (
          <SkeletonSet surface="table" rows={5} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load programs just now."
            onRetry={() => void list.refetch()}
          />
        ) : list.data && list.data.programs.length === 0 ? (
          reason === 'no_matches' ? (
            <FilteredEmpty
              onClear={() => {
                setQuery('');
                setStatus('default');
                setOffset(0);
              }}
            />
          ) : wholeOrg ? (
            <SuccessEmpty message="No programs yet. Programs help you organise your workforce, schedules and reporting — create one when you’re ready." />
          ) : (
            // A scoped reader's empty list is not an empty organization. "No
            // programs yet" plus "create one when you're ready" told somebody
            // whose grant covers programs they cannot currently see that their
            // organization has none — the withheld-as-absent inversion, with an
            // action attached.
            <SuccessEmpty
              heading="Nothing in the programs you were granted"
              message="This is not every program in the organization — your access covers only some of them, and none of those match. Someone with organization-wide access may see more."
            />
          )
        ) : (
          <>
            <TableCard
              columns={columns}
              rows={list.data?.programs ?? []}
              rowKey={(p) => p.id}
              onRowOpen={(p) => navigate(`/settings/programs/${p.id}`)}
              caption={wholeOrg ? 'Programs in this organization' : 'The programs you were granted'}
            />
            <div className="org-pager">
              <span className="rn-muted">
                {rangeLabel(
                  list.data?.total ?? 0,
                  PAGE_SIZE,
                  offset,
                  list.data?.programs.length ?? 0,
                )}
              </span>
              {/* The count's population, in the words `figureState` already has
                  for it. "1–25 of 40" is a fact about the organization only when
                  the server says it counted one. */}
              {scopeNote === null ? null : (
                <span className="rn-muted" role="note">
                  {scopeNote}
                </span>
              )}
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={!hasPreviousPage(offset)}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={!hasNextPage(list.data?.total ?? 0, PAGE_SIZE, offset)}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </SettingsCard>
    </div>
  );
}
